/**
 * Filet crochet charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const filetCrochetChartsPack: ToilePack = {
    slug: 'filet-crochet-charts',
    category: 'craft',
    name: 'Filet crochet charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'rose', label: 'Rose motif', subject: 'a square filet grid of about forty by forty cells with a single rose and two leaves in filled blocks, an open mesh ground, a solid one-block border round the edge'},
        {key: 'runner', label: 'Table runner', subject: 'a long filet chart for a table runner, a repeating band of grapes and vine leaves in filled blocks, the repeat bracketed and the row numbers running up the right edge'},
        {key: 'heart', label: 'Heart doily', subject: 'a filet chart with a large heart in filled blocks at the centre, an open mesh ring around it, and a stepped lacy border of alternating blocks and spaces'},
    ],
    styles: [
        {key: 'graph', label: 'Graph grid', style: 'crisp graph-paper grid, each filled block a solid black square and each mesh space left white, a heavier line every ten cells', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'crossed', label: 'Crossed blocks', style: 'thin grid with every filled block marked by a bold X in its cell and every mesh space left empty', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'lace', label: 'Lace view', style: 'the motif drawn as the finished lace, solid black blocks against open white mesh with no grid lines visible', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['a swan', 'a cat', 'curtain edge'],
    directionHint: 'try: a swan',
    seo: {
        title: 'Filet crochet charts: rose, table runner, heart doily',
        metaDescription: 'Nine filet crochet charts: a rose, a table runner band and a heart doily, in graph, crossed-block and lace styles.',
        h1: 'Filet crochet charts: rose, table runner and heart doily',
        tagline: 'Square grids where each filled cell is a solid block and each open cell is a mesh space.',
        intro: 'Filet crochet makes a picture from two kinds of square: a filled block and an open mesh space. A chart shows each as one cell on a grid, the way makers work doilies and runners. These nine cover a rose, a table runner band and a heart doily. Each comes as a graph grid, a grid with blocks marked by an X, or the finished lace. Type a different motif and the same grid carries it.',
        useCases: [
            'a doily with a central motif',
            'a table runner with a repeating band',
            'a curtain edge',
            'a first chart for filet work',
        ],
        faq: [
            {q: 'What does a filled cell mean?', a: 'A filled cell is a solid block, and an empty cell is an open mesh space.'},
            {q: 'Which style should I count from?', a: 'The graph grid is the one to count across. The crossed-block style marks each block with an X, and the lace view shows the finished look so you can check the design.'},
            {q: 'Can I change the motif?', a: 'Yes. Type the motif you want and the same grid layout carries it; the samples here stay as shown.'},
        ],
    },
}
